import { PhoneNumber } from './phoneNumber';

export enum DiamGroups {
  Husqvarna = 'husqvarna-mypages',
  Construction = 'construction-mypages'
}

export type CustomerUpdateForm = {
  firstName: string;
  lastName: string;
  email: string;
  dialCode: string;
  phoneNumber?: string;
  language?: string;
  country?: string;
  locationAddress: {
    street?: string;
    zipCode?: string;
    city?: string;
  };
  billingAddress: {
    street?: string;
    zipCode?: string;
    city?: string;
  };
  sameAsLocationAddress?: boolean;
  primaryContact: PrimaryContactWithPhoneNumber;
};

export type CustomerAccountStatus = {
  emailVerified: boolean;
  customerAccountExists: boolean;
  customerAccountVerified: boolean;
  loginAccountExists: boolean;
  loginAccountActive: boolean;
  customerType: string;
};

export type CustomerAccountStatusResponse = {
  data: {
    id: string;
    attributes: CustomerAccountStatus;
  };
};

export type DiamGroup = {
  groupId: string;
  name: string;
  groupType: string;
};

export type CreateCustomerAttributes = {
  companyName?: string;
  firstName: string;
  lastName: string;
  country: string;
  language: string;
  vatNumber?: string;
  phoneNumber?: string;
  type: CustomerType;
  email: string;
  password: string;
  locationAddress: Address;
  billingAddress: Address;
  shippingAddress: Address;
  captchaCode: string;
  groups: string[];
  primaryContact?: ParsedPrimaryContact;
};

export type CustomerConsentStatus = 'ACCEPTED' | 'DENIED';

export type CustomerConsentQuery = {
  customerId?: string | null;
  consentTypeId?: string;
  body?: {
    status: CustomerConsentStatus;
  };
};

export type CustomerConsentTypeText = {
  title: string;
  text: string;
};

export type CustomerConsentTranslationText = {
  [languageCode: string]: CustomerConsentTypeText;
};

export type CustomerConsentAttributes = {
  customerId: string;
  consentTypeId: string;
  status: CustomerConsentStatus | null;
  timestamp: number | null;
  texts: CustomerConsentTranslationText;
};

export type CustomerConsent = {
  id: string;
  type: string;
  attributes: CustomerConsentAttributes;
};

export type CustomerConsentResponse = {
  data: CustomerConsent[];
};

export type CustomerConsentUpdateResponse = {
  data: {
    id: string;
    type: string;
    attributes: {
      status: string;
    };
  };
};

export type ConsentType = {
  consentTypeId: string;
  status: CustomerConsentStatus;
};

export type Address = {
  city?: string;
  street?: string;
  zipCode?: string;
  country?: string;
  state?: string;
};

export type IAMUser = {
  groups: string[];
};

export type EContactEmail = {
  emailAddress: string;
};

export type EContactPhone = {
  phoneNumber: string;
};

export type EContact = {
  emailContacts: EContactEmail[];
  phoneContacts: EContactPhone[];
};

export enum CustomerType {
  IndividualCustomer = 'IndividualCustomer',
  OrganizationCustomer = 'OrganizationCustomer'
}

type PrimaryContactBase = {
  emailAddress: string;
  firstName: string;
  lastName: string;
};

export type ParsedPrimaryContact = PrimaryContactBase & {
  phoneNumber: string;
};

export type PrimaryContactWithPhoneNumber = PrimaryContactBase & {
  phoneNumber: PhoneNumber;
};

export type CustomerAttributes = {
  type: CustomerType;
  firstName: string;
  lastName: string;
  name?: string;
  country: string;
  language: string;
  vatNumber?: string;
  dunsNumber?: string;
  locationAddress?: Address;
  billingAddress?: Address;
  shippingAddress?: Address;
  eContact?: EContact;
  users: IAMUser[];
  primaryContact?: ParsedPrimaryContact;
  companyName?: string;
};

export type Customer = {
  id: string;
  attributes: CustomerAttributes;
};

export type CustomerResponse = {
  data: Customer;
};

export interface CustomerObj extends CustomerAttributes {
  id: string;
}

export interface ICustomerUpdate {
  firstName?: string;
  lastName?: string;
  billingAddress?: Address;
  locationAddress?: Address;
  shippingAddress?: Address;
  type: CustomerType;
  country?: string;
  language?: string;
  eContact?: {
    phoneContacts?: [
      {
        phoneNumber: string;
        isVerified: boolean;
      }
    ];
  };
  primaryContact?: Partial<ParsedPrimaryContact>;
}

export interface CustomerConsentType {
  id: string;
  texts: CustomerConsentTranslationText;
}

export type EmailType = 'eContact' | 'primaryContact';

export interface ICustomerChangeEmailAttributes {
  groups: DiamGroup[];
  iamUserId: string;
  newEmail: string;
  emailType: EmailType;
}

export interface ICustomerChangeEmailResponse {
  data: {
    type: 'customer';
    id: string;
    attributes: ICustomerChangeEmailAttributes;
  };
}

export interface ICustomerDeleteAccountAttributes {
  groups: DiamGroup[];
  email: string;
}

export interface ICustomerDeleteAccountResponse {
  data: {
    type: 'customer';
    id: string;
    attributes: ICustomerDeleteAccountAttributes;
  };
}
